/**
 * Product constants and mapping utilities
 * Centralized location for all product ID, key, and name mappings
 */

/**
 * Map API product IDs (PRD0001-PRD0007) to display names
 */
export const PRODUCT_ID_TO_NAME_MAP: Record<string, string> = {
	'PRD0001': 'Coffee',
	'PRD0002': 'French Vanilla',
	'PRD0003': 'Chocolate',
	'PRD0004': 'Milk',
	'PRD0005': 'Chai',
	'PRD0006': 'Coconut',
	'PRD0007': 'Oat'
};

/**
 * Map internal product keys to API product IDs
 */
export const PRODUCT_KEY_TO_ID_MAP: Record<string, string> = {
	'coffee': 'PRD0001',
	'french_vanilla': 'PRD0002',
	'chocolate': 'PRD0003',
	'milk': 'PRD0004',
	'chai': 'PRD0005',
	'coconut': 'PRD0006',
	'oat': 'PRD0007'
};

/**
 * Map API product names (BEANS, FV CREAM, etc.) to display names
 */
const API_NAME_TO_DISPLAY_MAP: Record<string, string> = {
	'BEANS': 'Coffee',
	'FV CREAM': 'French Vanilla',
	'OATMILK': 'Oat',
	'OAT': 'Oat',
	'COCOA': 'Cocoa',
	'MILK': 'Milk',
	'CHAI': 'Chai',
	'COCONUT': 'Coconut',
	'MATCHA': 'Matcha'
};

/**
 * Get display name from product ID (PRD0001-PRD0007)
 */
export function getProductDisplayName(productId: string): string {
	return PRODUCT_ID_TO_NAME_MAP[productId] || productId;
}

/**
 * Get product ID from internal key (coffee, french_vanilla, etc.)
 */
export function getProductIdFromKey(key: string): string {
	return PRODUCT_KEY_TO_ID_MAP[key] || key;
}

/**
 * Get internal key from product ID (reverse lookup)
 */
export function getProductKeyFromId(productId: string): string {
	const reverseMap: Record<string, string> = {};
	for (const [key, id] of Object.entries(PRODUCT_KEY_TO_ID_MAP)) {
		reverseMap[id] = key;
	}
	return reverseMap[productId] || productId;
}

/**
 * Map API product name (BEANS, FV CREAM, etc.) to display name
 * Handles variations like OATMILK/OAT, FV CREAM, etc.
 */
export function mapApiProductNameToDisplayName(apiProductName: string): string {
	if (!apiProductName) {
		return apiProductName;
	}

	const normalized = apiProductName.toUpperCase();
	const displayName = API_NAME_TO_DISPLAY_MAP[normalized];

	if (displayName) {
		return displayName;
	}

	// Fallback: capitalize first letter of each word
	return apiProductName
		.split(' ')
		.map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
		.join(' ');
}

/**
 * Map API product name to internal product key
 * Maps: BEANS -> coffee, FV CREAM -> french_vanilla, OATMILK/OAT -> oat, etc.
 */
export function mapApiProductNameToKey(apiProductName: string): string {
	if (!apiProductName) {
		return apiProductName;
	}

	const normalized = apiProductName.toUpperCase();

	const apiNameToKeyMap: Record<string, string> = {
		'BEANS': 'coffee',
		'FV CREAM': 'french_vanilla',
		'OATMILK': 'oat',
		'OAT': 'oat',
		'COCOA': 'cocoa',
		'MILK': 'milk',
		'CHAI': 'chai',
		'COCONUT': 'coconut',
		'MATCHA': 'matcha'
	};

	const key = apiNameToKeyMap[normalized];
	if (key) {
		return key;
	}

	// Fallback: convert to lowercase and remove spaces
	return apiProductName.toLowerCase().replace(/\s+/g, '');
}


